import type { CanvasSectionBlockLayout } from '../sectionRegistry';
import {
  calculateMoveAlignmentSnap,
  createAlignmentBoxFromLayout,
  createAlignmentBoxesFromLayouts,
  translateAlignmentBox,
  type AlignmentLayoutBlock,
} from './alignmentMath';
import type { AlignmentGuides, TransformInteractionState } from './types';

export type LayoutSnapBlock = AlignmentLayoutBlock;

export function clampLayoutToBounds(
  layout: CanvasSectionBlockLayout,
  maxRows: number,
  totalColumns: number
): CanvasSectionBlockLayout {
  const x = Math.max(1, Math.min(totalColumns, Math.round(layout.x)));
  const y = Math.max(1, Math.min(maxRows, Math.round(layout.y)));
  const w = Math.max(1, Math.min(totalColumns - x + 1, Math.round(layout.w)));
  const h = Math.max(1, Math.min(maxRows - y + 1, Math.round(layout.h)));
  return { x, y, w, h };
}

export function clampHandleLayoutToBounds(
  start: CanvasSectionBlockLayout,
  handle: TransformInteractionState['handle'],
  layout: CanvasSectionBlockLayout,
  maxRows: number,
  totalColumns: number
): CanvasSectionBlockLayout {
  if (handle === 'move') {
    return clampLayoutToBounds(layout, maxRows, totalColumns);
  }

  const startRight = start.x + start.w - 1;
  const startBottom = start.y + start.h - 1;

  let x = start.x;
  let y = start.y;
  let w = start.w;
  let h = start.h;

  if (handle === 'e' || handle === 'ne' || handle === 'se') {
    x = start.x;
    w = Math.max(1, Math.min(totalColumns - start.x + 1, Math.round(layout.w)));
  } else if (handle === 'w' || handle === 'nw' || handle === 'sw') {
    x = Math.max(1, Math.min(startRight, Math.round(layout.x)));
    w = Math.max(1, startRight - x + 1);
  }

  if (handle === 's' || handle === 'se' || handle === 'sw') {
    y = start.y;
    h = Math.max(1, Math.min(maxRows - start.y + 1, Math.round(layout.h)));
  } else if (handle === 'n' || handle === 'ne' || handle === 'nw') {
    y = Math.max(1, Math.min(startBottom, Math.round(layout.y)));
    h = Math.max(1, startBottom - y + 1);
  }

  return { x, y, w, h };
}

export function applyHandleDelta(
  start: CanvasSectionBlockLayout,
  handle: TransformInteractionState['handle'],
  deltaCols: number,
  deltaRows: number
): CanvasSectionBlockLayout {
  let next: CanvasSectionBlockLayout = { ...start };

  switch (handle) {
    case 'move':
      next = { ...next, x: start.x + deltaCols, y: start.y + deltaRows };
      break;
    case 'n':
      next = { ...next, y: start.y + deltaRows, h: start.h - deltaRows };
      break;
    case 's':
      next = { ...next, h: start.h + deltaRows };
      break;
    case 'e':
      next = { ...next, w: start.w + deltaCols };
      break;
    case 'w':
      next = { ...next, x: start.x + deltaCols, w: start.w - deltaCols };
      break;
    case 'ne':
      next = { ...next, y: start.y + deltaRows, h: start.h - deltaRows, w: start.w + deltaCols };
      break;
    case 'nw':
      next = {
        ...next,
        x: start.x + deltaCols,
        w: start.w - deltaCols,
        y: start.y + deltaRows,
        h: start.h - deltaRows,
      };
      break;
    case 'se':
      next = { ...next, w: start.w + deltaCols, h: start.h + deltaRows };
      break;
    case 'sw':
      next = { ...next, x: start.x + deltaCols, w: start.w - deltaCols, h: start.h + deltaRows };
      break;
    default:
      break;
  }

  return next;
}

export function layoutsEqual(a: CanvasSectionBlockLayout, b: CanvasSectionBlockLayout) {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

export function applyMoveSnap(params: {
  nextLayout: CanvasSectionBlockLayout;
  interaction: TransformInteractionState;
  blockId: string;
  blocks: LayoutSnapBlock[];
  totalColumns: number;
  snapThresholdPx: number;
  maxRows: number;
}): { layout: CanvasSectionBlockLayout; guides: AlignmentGuides } {
  const { nextLayout, interaction, blockId, blocks, totalColumns, snapThresholdPx, maxRows } = params;
  if (interaction.handle !== 'move') return { layout: nextLayout, guides: {} };

  const layerWidth = interaction.colWidth * totalColumns;
  const layerHeight = interaction.rowHeight * maxRows;
  const layoutDeltaX = (nextLayout.x - interaction.startLayout.x) * interaction.colWidth;
  const layoutDeltaY = (nextLayout.y - interaction.startLayout.y) * interaction.rowHeight;
  const movingBox = interaction.startEditorBox
    ? translateAlignmentBox(interaction.startEditorBox, layoutDeltaX, layoutDeltaY)
    : createAlignmentBoxFromLayout(nextLayout, interaction.colWidth, interaction.rowHeight);
  const referenceBoxes = createAlignmentBoxesFromLayouts(
    blocks.filter((block) => block.id !== blockId),
    interaction.colWidth,
    interaction.rowHeight
  );
  const snapped = calculateMoveAlignmentSnap({
    movingBox,
    referenceBoxes,
    sectionWidth: layerWidth,
    sectionHeight: layerHeight,
    threshold: snapThresholdPx,
  });
  const snappedX = nextLayout.x + snapped.offsetXDelta / interaction.colWidth;
  const snappedY = nextLayout.y + snapped.offsetYDelta / interaction.rowHeight;

  return {
    layout: clampLayoutToBounds(
      { ...nextLayout, x: snappedX, y: snappedY },
      maxRows,
      totalColumns
    ),
    guides: snapped.guides,
  };
}
